/**
 * Which campaigns screen a URL points at, for the places that only know the
 * path and must draw that screen's loading shape before its chunk arrives:
 * `RouteSkeleton`, the agent layouts while the agent loads, and the routes'
 * Suspense fallbacks.
 *
 * The four routes `router/AppRouter.tsx` mounts under the agent's `myassistant`
 * group (written as plain strings there; `../../paths.ts`):
 *
 * - `list`    — `…/myassistant/campaigns`
 * - `create`  — `…/myassistant/campaigns/new`
 * - `detail`  — `…/myassistant/campaigns/:campaignId`
 * - `results` — `…/myassistant/campaigns/:campaignId/table`
 *
 * Anchored at both ends, so anything nested deeper, another agent route that
 * happens to contain `campaigns`, or the account-level paths never match. `new`
 * is the wizard, never a campaign id — the router ranks the static segment
 * above the dynamic one, and so does this. No lookbehind: Babel can't
 * transpile one and Safari < 16.4 rejects the whole bundle.
 */
export type CampaignsSkeletonKind = 'list' | 'create' | 'detail' | 'results';

const BASE = '^/accounts/[^/]+/assistants/[^/]+/myassistant/campaigns';

const LIST_ROUTE = new RegExp(`${BASE}/?$`);
const CREATE_ROUTE = new RegExp(`${BASE}/new/?$`);
const DETAIL_ROUTE = new RegExp(`${BASE}/[^/]+/?$`);
const RESULTS_ROUTE = new RegExp(`${BASE}/[^/]+/table/?$`);

export function campaignsSkeletonKind(pathname: string): CampaignsSkeletonKind | null {
  if (LIST_ROUTE.test(pathname)) return 'list';
  // Before the detail: `new` would otherwise read as a campaign id.
  if (CREATE_ROUTE.test(pathname)) return 'create';
  if (DETAIL_ROUTE.test(pathname)) return 'detail';
  if (RESULTS_ROUTE.test(pathname)) return 'results';
  return null;
}

export function isCampaignsPath(pathname: string): boolean {
  return campaignsSkeletonKind(pathname) !== null;
}
